import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { EntitySocialLinkIcons } from "@/components/EntitySocialLinkIcons";
import { supabase } from "@/lib/supabase";
import { formatNewsMonthYear } from "@/lib/displayOrderDates";
import {
  normalizeExternalHref,
  type EntitySocialLinksFields,
} from "@/lib/entitySocialLinks";

type Assoc = {
  name: string;
  description: string | null;
  logo_url: string | null;
  exco_photo_url: string | null;
} & EntitySocialLinksFields;

type Project = {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  link_url: string | null;
  created_at: string | null;
  updated_at: string | null;
};

type NewsItem = {
  id: string;
  slug: string | null;
  title: string;
  description: string | null;
  image_url: string | null;
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export function AssociationDetailPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [found, setFound] = useState(false);
  const [row, setRow] = useState<Assoc | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [news, setNews] = useState<NewsItem[]>([]);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    void supabase
      .from("associations")
      .select(
        "id,name,description,logo_url,exco_photo_url,social_facebook_url,social_instagram_url,social_youtube_url,social_website_url,social_other_url"
      )
      .eq("slug", slug)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) {
          setFound(false);
          setRow(null);
          setProjects([]);
          setNews([]);
          setLoading(false);
          return;
        }
        setFound(true);
        const { id, ...rest } = data as { id: string } & Assoc;
        setRow(rest);
        void Promise.all([
          supabase
            .from("projects")
            .select("id,title,description,image_url,link_url,created_at,updated_at")
            .eq("association_id", id)
            .order("updated_at", { ascending: false })
            .then(({ data: p }) => setProjects((p as Project[]) ?? [])),
          supabase
            .from("news")
            .select("id,slug,title,description,image_url,published_at,created_at,updated_at")
            .eq("association_id", id)
            .order("updated_at", { ascending: false })
            .then(({ data: n }) => setNews((n as NewsItem[]) ?? [])),
        ]).then(() => setLoading(false));
      });
  }, [slug]);

  if (!slug) return null;

  if (!loading && !found) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center text-brand-blue/70">
        Association not found.
        <div className="mt-6">
          <Link
            to="/alumni/associations"
            className="text-brand-maroon font-semibold"
          >
            ← All associations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-14">
      <Link
        to="/alumni/associations"
        className="text-sm font-semibold text-brand-maroon hover:underline"
      >
        ← Associations
      </Link>
      {loading ? (
        <p className="mt-8 text-brand-blue/60">Loading…</p>
      ) : row ? (
        <>
          <div className="mt-6 flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-8">
            {row.logo_url ? (
              <div className="flex shrink-0 justify-center sm:justify-start">
                <img
                  src={row.logo_url}
                  alt={`${row.name} logo`}
                  className="h-24 max-w-[220px] object-contain sm:h-28"
                />
              </div>
            ) : null}
            <h1 className="font-display text-4xl font-bold text-brand-blue sm:flex-1">
              {row.name}
            </h1>
          </div>
          <EntitySocialLinkIcons links={row} className="mt-4" />
          {row.exco_photo_url && (
            <img
              src={row.exco_photo_url}
              alt=""
              className="mt-8 w-full max-h-[420px] rounded-2xl object-cover"
            />
          )}
          {row.description && (
            <p className="mt-8 whitespace-pre-line text-brand-blue/90 leading-relaxed">
              {row.description}
            </p>
          )}
        </>
      ) : null}

      {!loading && (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-bold text-brand-blue">
            Projects
          </h2>
          {projects.length === 0 ? (
            <p className="mt-4 text-sm text-brand-blue/60">No projects listed.</p>
          ) : (
            <ul className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((p) => {
                const projectMonth = formatNewsMonthYear(p.updated_at ?? p.created_at);
                const safeLink = normalizeExternalHref(p.link_url);
                return (
                  <li
                    key={p.id}
                    className="flex h-full flex-col overflow-hidden rounded-2xl border border-brand-gray bg-brand-gray/30"
                  >
                    <Link
                      to={`/projects/${p.id}`}
                      className="flex min-h-0 flex-1 flex-col text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-maroon focus-visible:ring-offset-2"
                      aria-label={p.title ? `View project: ${p.title}` : "View project"}
                    >
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt=""
                          loading="lazy"
                          className="h-44 w-full shrink-0 object-cover"
                        />
                      ) : null}
                      <div className="flex min-h-0 flex-1 flex-col p-5">
                        {projectMonth ? (
                          <p className="text-xs font-semibold uppercase tracking-wide text-brand-maroon">
                            {projectMonth}
                          </p>
                        ) : null}
                        <h3 className="mt-1 font-display text-xl font-bold text-brand-blue">{p.title}</h3>
                        {p.description ? (
                          <p className="mt-2 line-clamp-4 text-sm text-brand-blue/85 whitespace-pre-line">
                            {p.description}
                          </p>
                        ) : null}
                      </div>
                    </Link>
                    {safeLink ? (
                      <div className="border-t border-brand-gray/40 px-5 py-3">
                        <a
                          href={safeLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-bold text-brand-maroon hover:underline"
                        >
                          Open external link →
                        </a>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {!loading && (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-bold text-brand-blue">
            News
          </h2>
          {news.length === 0 ? (
            <p className="mt-4 text-sm text-brand-blue/60">No news listed.</p>
          ) : (
            <ul className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {news.map((n) => {
                const dateLine = formatNewsMonthYear(n.updated_at ?? n.created_at);
                const detailHref = n.slug ? `/news/other/${n.slug}` : "/news/other";
                return (
                  <li key={n.id} className="h-full">
                    <Link
                      to={detailHref}
                      className="flex h-full flex-col overflow-hidden rounded-2xl border border-brand-gray bg-white shadow-sm transition hover:border-brand-maroon/35 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-maroon"
                      aria-label={n.title ? `Read article: ${n.title}` : "Read article"}
                    >
                      <div className="aspect-[16/9] shrink-0 bg-brand-gray">
                        {n.image_url ? (
                          <img
                            src={n.image_url}
                            alt=""
                            loading="lazy"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-xs text-brand-blue/50">
                            No image
                          </div>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        {dateLine ? (
                          <time
                            dateTime={n.updated_at ?? n.created_at ?? undefined}
                            className="text-xs font-semibold uppercase tracking-wide text-brand-maroon"
                          >
                            {dateLine}
                          </time>
                        ) : null}
                        <h3 className="mt-2 font-display text-xl font-bold text-brand-blue">
                          {n.title}
                        </h3>
                        {n.description ? (
                          <p className="mt-2 line-clamp-4 text-sm text-brand-blue/85 whitespace-pre-line">
                            {n.description}
                          </p>
                        ) : null}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
